import { findTicketOptionForProduct } from "../../../Lib/Utils/ticketOptionMatcher";
import { determinePassengerType } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IPassenger, IPassengerSelection } from "../../../Orchestrator/Types/Index";
import { ITicketOption } from "../../../Orchestrator/Types/Interface/ITicketOption";
import dayjs from "dayjs";
import { IPassBooking } from "../../../Orchestrator/Types/Types/BookingTypes";
import ticketOptionManager from "../../../Lib/Utils/TicketOptionManager";

export interface IPassBookingSummary extends IPassBooking {
  summary: {
    originCode?: string;
    destinationCode?: string;
    validFrom: string;
    validUntil: string;
    duration: {
      total: number;
      remaining: number;
    };
    isExpired: boolean;
    ticket: ITicketOption | null;
    passenger: IPassenger[],
    passengerType: IPassengerSelection[]
  }
}

export const getPassBookingSummary = (passBooking: IPassBooking): IPassBookingSummary => {
  const passengerTypesStateAtom = getRecoilStateValue<IPassengerSelection[]>("passengerTypesStateAtom");

  // Ticket Info — match the booked product against the meta records via
  // productTags + serviceClass.type (see findTicketOptionForProduct).
  // Resilient to new backend product codes that the legacy hardcoded
  // list silently dropped.
  const bookedProduct = passBooking.booking?.booking?.bookedOffers?.[0]?.products?.[0];
  const ticketOptions = ticketOptionManager.getAllOptions();
  const ticketOption: ITicketOption | null = findTicketOptionForProduct(
    bookedProduct,
    ticketOptions,
  );

  // Passenger Info
  const passengerTypes = passBooking.booking?.booking.passengers?.map(passengerInfo => {
    const determinePassenger = {
      age: passengerInfo.age || 0,
      cards: passengerInfo?.cards || [],
      prmNeeds: ((passengerInfo?.prmNeeds || []).length > 0 && passengerInfo?.prmNeeds) ? passengerInfo?.prmNeeds[0] : ""
    }
    const passengerType = determinePassengerType(determinePassenger)
    return passengerTypesStateAtom?.find((d: any) => d.id === passengerType) || null;
  }).filter(d => d !== null) || [];

  // Duration
  //
  // Optional-chained throughout: a booking whose travel account has not been
  // provisioned yet reaches here with no `travelAccount`, and a throw takes the
  // whole detail page down rather than one row.
  const processDuration = () => {
    const offerBalance = passBooking.travelAccount?.travelAccount?.balance
    if (passBooking.travelAccount?.travelAccount?.objectType === "MultiRideAccount" && offerBalance) {
      return {
        total: offerBalance.total,
        remaining: offerBalance.remaining
      }
    } else if (passBooking.travelAccount?.travelAccount?.objectType === "TravelPassAccount") {
      const validFrom = dayjs(passBooking.travelAccount?.travelAccount?.validFrom);
      const validUntil = dayjs(passBooking.travelAccount?.travelAccount?.validUntil);

      let remainingDays = dayjs().isAfter(validFrom) ? validUntil.diff(dayjs(), 'day') : validUntil.diff(validFrom, 'day')
      return {
        total: validUntil.diff(validFrom, 'day'),
        remaining: remainingDays < 0 ? 0 : remainingDays
      }
    }
    return { total: 0, remaining: 0 }
  }

  const validities = passBooking?.travelAccount?.travelAccount?.regionalConstraint?.regionalValidities?.[0]
  const duration = processDuration();

  const validUntil = dayjs(passBooking.travelAccount?.travelAccount?.validUntil);

  return {
    ...passBooking,
    summary: {
      originCode: validities?.zone.entryStation.ref.stopPlaceRef,
      destinationCode: validities?.zone.terminalStation.ref.stopPlaceRef,
      validFrom: passBooking.travelAccount?.travelAccount?.validFrom,
      validUntil: passBooking.travelAccount?.travelAccount?.validUntil,
      duration: duration,
      isExpired: duration.remaining <= 0 || dayjs().isAfter(validUntil),
      ticket: ticketOption,
      passenger: passBooking.booking?.booking.passengers,
      passengerType: passengerTypes || []
    }
  }
}
